import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, ShieldCheck, X, Upload, FileText } from "lucide-react";
import { PortalHeader, PortalCard, PortalEmpty } from "@/components/portal/PortalHeader";
import { getMyProject, getMyWarranty, submitMyWarrantyClaim, getMyFileSignedUrl } from "@/lib/portal/portal.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/portal/warranty")({
  component: WarrantyPage,
});

const STATUS_COLORS: Record<string, string> = {
  submitted: "bg-amber-300/15 text-amber-200 border-amber-300/30",
  in_review: "bg-blue-400/15 text-blue-300 border-blue-400/30",
  scheduled: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  resolved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  denied: "bg-red-500/15 text-red-300 border-red-500/30",
};

function WarrantyPage() {
  const qc = useQueryClient();
  const projectFn = useServerFn(getMyProject);
  const warrantyFn = useServerFn(getMyWarranty);
  const submitFn = useServerFn(submitMyWarrantyClaim);

  const projectQ = useQuery({ queryKey: ["portal", "project"], queryFn: () => projectFn() });
  const projectId = projectQ.data?.project?.id;
  const wQ = useQuery({
    queryKey: ["portal", "warranty", projectId],
    queryFn: () => warrantyFn({ data: { projectId: projectId! } }),
    enabled: !!projectId,
  });

  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  if (projectQ.isLoading || wQ.isLoading) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!projectId) return <PortalEmpty title="No project yet" />;

  const { warranty, claims } = wQ.data!;

  const submit = async () => {
    if (desc.trim().length < 5) return;
    setBusy(true);
    try {
      const photoPaths: string[] = [];
      for (const file of files) {
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const path = `${projectId}/warranty/${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage.from("project-files").upload(path, file, { contentType: file.type });
        if (error) throw error;
        photoPaths.push(path);
      }
      await submitFn({ data: { projectId, description: desc.trim(), photos: photoPaths } });
      setOpen(false); setDesc(""); setFiles([]);
      await qc.invalidateQueries({ queryKey: ["portal", "warranty", projectId] });
    } catch (e: any) { alert(e?.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PortalHeader eyebrow="Warranty" title="Coverage & claims"
        description="Your written warranty is backed by Michael's Painting. Submit a request anytime — we'll respond within one business day."
        actions={<button onClick={() => setOpen(true)} className="bg-amber-300 hover:bg-amber-200 text-zinc-950 text-xs font-semibold px-4 py-2 rounded-full">Submit warranty request</button>}
      />

      <PortalCard className="p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="size-12 rounded-2xl bg-amber-300/10 border border-amber-300/20 grid place-items-center"><ShieldCheck className="size-6 text-amber-300" /></div>
          <div className="flex-1">
            {warranty ? (
              <>
                <p className="text-[11px] uppercase tracking-[0.25em] text-amber-300 font-semibold">{warranty.coverage_years}-year coverage</p>
                <p className="text-white mt-1">In effect through <strong>{warranty.expires_on ? new Date(warranty.expires_on).toLocaleDateString() : "—"}</strong></p>
                <p className="text-xs text-zinc-500 mt-1">Began {new Date(warranty.starts_on).toLocaleDateString()}</p>
                {warranty.details && <p className="text-sm text-zinc-300 leading-relaxed mt-4 whitespace-pre-wrap">{warranty.details}</p>}
                {warranty.document_url && (
                  <a href={warranty.document_url} target="_blank" rel="noopener" className="inline-flex items-center gap-1.5 mt-4 text-xs text-amber-300 hover:underline">
                    <FileText className="size-3.5" /> View warranty document
                  </a>
                )}
              </>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-[0.25em] text-amber-300 font-semibold">Pending</p>
                <p className="text-white mt-1">Your warranty activates when the final walkthrough is complete.</p>
              </>
            )}
          </div>
        </div>
      </PortalCard>

      <h2 className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">Your warranty requests</h2>
      {claims.length === 0 ? (
        <PortalEmpty title="No requests yet" description="If you ever notice anything off, click 'Submit warranty request' above." />
      ) : (
        <div className="space-y-2">
          {claims.map((c) => <ClaimRow key={c.id} claim={c} projectId={projectId} />)}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0a0a0a] p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-zinc-500"><X className="size-5" /></button>
            <h2 className="font-serif text-2xl text-white">New warranty request</h2>
            <p className="text-xs text-zinc-500 mt-1">Describe what you're seeing. Add photos if helpful.</p>

            <label className="block mt-5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Describe the issue</label>
            <textarea rows={5} value={desc} onChange={(e) => setDesc(e.target.value)}
              placeholder="e.g. Small section of paint near the front entry began peeling after the recent humidity."
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white resize-none" />

            <label className="block mt-4 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Photos (optional)</label>
            <label className="mt-1 flex items-center justify-center gap-2 border border-dashed border-white/15 rounded-xl py-6 cursor-pointer hover:border-amber-300/40 text-sm text-zinc-400">
              <Upload className="size-4" /> {files.length ? `${files.length} file(s) attached` : "Upload photos"}
              <input type="file" multiple accept="image/*" className="hidden"
                onChange={(e) => setFiles([...files, ...Array.from(e.target.files ?? [])])} />
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="text-sm text-zinc-400 px-4 py-2">Cancel</button>
              <button onClick={submit} disabled={busy || desc.trim().length < 5}
                className="bg-amber-300 hover:bg-amber-200 text-zinc-950 text-sm font-semibold px-5 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-50">
                {busy && <Loader2 className="size-4 animate-spin" />} Submit request
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ClaimRow({ claim, projectId }: { claim: any; projectId: string }) {
  const signFn = useServerFn(getMyFileSignedUrl);
  const photos: string[] = Array.isArray(claim.photos) ? claim.photos : [];
  const [signed, setSigned] = useState<Record<string, string>>({});

  const openPhoto = async (path: string) => {
    if (/^https?:\/\//i.test(path)) { window.open(path, "_blank"); return; }
    if (signed[path]) { window.open(signed[path], "_blank"); return; }
    try {
      const r = await signFn({ data: { projectId, path } });
      setSigned((s) => ({ ...s, [path]: r.url })); window.open(r.url, "_blank");
    } catch (e: any) { alert(e?.message || "Failed"); }
  };

  return (
    <PortalCard className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <p className="text-[11px] text-zinc-500">{new Date(claim.created_at).toLocaleString()}</p>
        <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${STATUS_COLORS[claim.status] ?? STATUS_COLORS.submitted}`}>
          {claim.status.replace("_", " ")}
        </span>
      </div>
      <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{claim.description}</p>
      {photos.length > 0 && (
        <div className="mt-3 flex gap-1.5 flex-wrap">
          {photos.map((p, i) => (
            <button key={i} onClick={() => openPhoto(p)} className="text-[11px] px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-amber-200">
              Photo {i + 1}
            </button>
          ))}
        </div>
      )}
      {claim.admin_notes && (
        <div className="mt-3 rounded-lg bg-amber-300/5 border border-amber-300/20 p-3">
          <p className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold">Team response</p>
          <p className="text-sm text-zinc-200 mt-1 whitespace-pre-wrap">{claim.admin_notes}</p>
        </div>
      )}
    </PortalCard>
  );
}